import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Eye, RefreshCw, ExternalLink } from "lucide-react";
import { enrollmentService } from "../../services/enrollmentService";

interface Enrollment {
  _id: string;
  user?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    email?: string;
  } | null;
  course?: {
    _id: string;
    title?: string;
  } | null;
  status: string;
  enrollmentDate: string;
}

export function EnrollmentTable() {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRecentEnrollments = async () => {
    setIsLoading(true);
    try {
      const response = await enrollmentService.getAllEnrollments({ limit: 5 });
      const data = (response.enrollments || []) as unknown as Enrollment[];
      // Sort by date descending
      data.sort(
        (a, b) =>
          new Date(b.enrollmentDate).getTime() -
          new Date(a.enrollmentDate).getTime(),
      );
      setEnrollments(data.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch enrollments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentEnrollments();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "expired":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">
          Recent Students Enrollments
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRecentEnrollments}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </Button>
          <Link to="/enrollments">
            <Button variant="primary" size="sm" className="gap-2">
              View All <ExternalLink size={14} />
            </Button>
          </Link>
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Student
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Course
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Enroll Date
                </th>
                <th className="px-3 sm:px-6 py-4 w-[50px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    <RefreshCw className="w-5 h-5 animate-spin inline-block mr-2" />
                    Loading...
                  </td>
                </tr>
              ) : enrollments.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No recent enrollments found
                  </td>
                </tr>
              ) : (
                enrollments.map((enrollment) => (
                  <tr
                    key={enrollment._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {enrollment.user?.firstName &&
                          enrollment.user?.lastName
                            ? `${enrollment.user.firstName} ${enrollment.user.lastName}`
                            : enrollment.user?.username || "Unknown"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {enrollment.user?.email || "No email"}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 font-medium text-gray-900">
                      {enrollment.course?.title || "Unknown Course"}
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                          enrollment.status,
                        )}`}
                      >
                        {enrollment.status}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-gray-500">
                      {formatDate(enrollment.enrollmentDate)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right">
                      {enrollment.user?._id && (
                        <Link
                          to={`/students/${enrollment.user._id}`}
                          className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors inline-block"
                        >
                          <Eye size={18} />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
          <span>Showing {enrollments.length} recent enrollments</span>
          <Link
            to="/enrollments"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            View all enrollments →
          </Link>
        </div>
      </Card>
    </div>
  );
}
